import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/container";
import Breadcrumbs from "@/components/ui/breadcrumbs";
import SEO from "@/components/SEO";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import WhatsAppFloat from "@/components/ui/whatsapp-float";
import { Shield, Eye, Lock, Users, FileText, Mail } from "lucide-react";

const Privacy = () => {
  const sections = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Coleta de Informações",
      content: [
        "Coletamos apenas as informações necessárias para prestar nossos serviços de consultoria e desenvolvimento Fluig.",
        "Dados pessoais como nome, e-mail, telefone e empresa são coletados através de formulários de contato e solicitações de proposta.",
        "Informações técnicas do navegador podem ser coletadas automaticamente para melhorar a experiência do usuário.",
        "Não coletamos dados sensíveis sem consentimento explícito."
      ]
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Uso das Informações",
      content: [
        "Os dados são utilizados exclusivamente para responder suas solicitações e prestar nossos serviços.",
        "Informações de contato podem ser usadas para envio de materiais relevantes sobre soluções Fluig.",
        "Dados técnicos ajudam a melhorar nosso site e personalizar a experiência.",
        "Não vendemos, alugamos ou compartilhamos seus dados com terceiros sem consentimento."
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Proteção de Dados",
      content: [
        "Utilizamos medidas de segurança adequadas para proteger suas informações.",
        "Dados são armazenados em servidores seguros com criptografia SSL.",
        "Acesso às informações é restrito apenas a funcionários autorizados.",
        "Realizamos backups regulares e monitoramento de segurança contínuo."
      ]
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Seus Direitos",
      content: [
        "Você tem direito de acessar, corrigir ou excluir seus dados pessoais.",
        "Pode solicitar a portabilidade dos seus dados a qualquer momento.",
        "Tem o direito de revogar o consentimento para uso dos dados.",
        "Pode solicitar informações sobre como seus dados estão sendo utilizados."
      ]
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: "Cookies e Tecnologias",
      content: [
        "Utilizamos cookies para melhorar a funcionalidade do site.",
        "Cookies de sessão são usados para manter a navegação ativa.",
        "Ferramentas de análise (Google Analytics) podem ser utilizadas para estatísticas.",
        "Você pode desabilitar cookies nas configurações do seu navegador."
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Contato e Alterações",
      content: [
        "Para exercer seus direitos ou esclarecer dúvidas, entre em contato conosco.",
        "Esta política pode ser atualizada para refletir mudanças em nossas práticas.",
        "Alterações significativas serão comunicadas através do site.",
        "A data da última atualização está sempre disponível no final desta política."
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO 
        title="Política de Privacidade - Future Station"
        description="Como protegemos seus dados pessoais conforme a LGPD. Transparência total no tratamento de informações, segurança SSL e direitos garantidos aos usuários."
        keywords={['política privacidade future station', 'lgpd compliance', 'proteção dados pessoais', 'privacidade online', 'segurança informações']}
        type="website"
      />
      <Header />
      
      <main className="pt-24">
        {/* Breadcrumbs */}
        <Container className="py-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumbs />
          </div>
        </Container>
        
        {/* Hero Section */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-fs-purple-500/20 rounded-full">
                <Shield className="w-12 h-12 text-fs-purple-500" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 fs-gradient-text">
              Política de Privacidade
            </h1>
            <p className="text-xl text-muted-foreground dark:text-white/70 max-w-3xl mx-auto mb-8">
              Seu compromisso com a proteção dos seus dados pessoais é fundamental para nós. 
              Entenda como coletamos, usamos e protegemos suas informações.
            </p>
            <div className="text-sm text-muted-foreground dark:text-white/60">
              Última atualização: Janeiro de 2025
            </div>
          </div>
        </Container>

        {/* Content Sections */}
        <Container className="py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid gap-8">
              {sections.map((section, index) => (
                <Card key={index} className="bg-card/80 backdrop-blur-sm border-border shadow-md dark:shadow-none hover:border-fs-purple-500/40 transition-all duration-300">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-3 text-2xl text-foreground dark:text-white">
                      <div className="text-fs-purple-500">
                        {section.icon}
                      </div>
                      <span>{section.title}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {section.content.map((item, itemIndex) => (
                        <p key={itemIndex} className="text-muted-foreground dark:text-white/70 leading-relaxed">
                          • {item}
                        </p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </Container>

        {/* LGPD Compliance */}
        <Container className="py-20">
          <div className="max-w-4xl mx-auto">
            <Card className="bg-gradient-to-br from-fs-purple-500/10 to-fs-cyan-500/10 border-fs-purple-500/20">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-foreground dark:text-white mb-6 flex items-center">
                  <Shield className="w-6 h-6 mr-3 text-fs-purple-500" />
                  Conformidade LGPD
                </h2>
                <div className="space-y-4 text-muted-foreground dark:text-white/70">
                  <p>
                    A Future Station está em total conformidade com a Lei Geral de Proteção de Dados (LGPD - Lei 13.709/2018) 
                    e se compromete a processar dados pessoais de forma transparente, segura e respeitando todos os direitos dos titulares.
                  </p>
                  <p>
                    <strong>Controlador de Dados:</strong> Future Station<br/>
                    <strong>Encarregado de Dados:</strong> Leonardo Manga<br/>
                    <strong>Contato:</strong> contato@futurestation.com.br<br/>
                    <strong>WhatsApp:</strong> +55 (81) 98165-8439
                  </p>
                  <p className="text-sm">
                    Para exercer seus direitos conforme a LGPD ou esclarecer dúvidas sobre o tratamento dos seus dados, 
                    entre em contato conosco através dos canais informados acima.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </Container>
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Privacy;